import React, { useCallback, useEffect, useRef, useState } from 'react';
import TopBar from './TopBar';
import Icon from './Icon';
import Window from './Window';
import { getLocalizedAppLabel, normalizeLanguage } from '../utils/i18n';
import { normalizeOsMode, resolveDesktopLayout } from '../utils/os';

const INTERNAL_WINDOW_IDS = ['settings', 'profile', 'resume', 'projects', 'ai-auth', 'ai-chat', 'change-os'];
const DOCK_FIXED_IDS = ['settings', 'profile', 'resume', 'projects', 'ai-chat', 'change-os'];
const AI_TOOL_WINDOW_IDS = ['ai-auth', 'ai-chat', 'profile', 'resume', 'projects'];
const MINIMIZE_ANIMATION_MS = 380;
const RESTORE_ANIMATION_MS = 360;
const ADMIN_TAB_ID_STORAGE_KEY = 'devfolio-admin-tab-id';
const ADMIN_AUTH_STORAGE_PREFIX = 'devfolio-admin-auth:';
const ADMIN_AUTH_TTL_MS = 5 * 60 * 1000;

const getAdminAuthStorageKey = (tabId) => `${ADMIN_AUTH_STORAGE_PREFIX}${tabId}`;

const readAdminSessionState = (tabId) => {
  if (!tabId) {
    return false;
  }

  const rawValue = sessionStorage.getItem(getAdminAuthStorageKey(tabId));
  if (!rawValue) {
    return false;
  }

  if (rawValue === 'true') {
    sessionStorage.removeItem(getAdminAuthStorageKey(tabId));
    return false;
  }

  try {
    const parsedValue = JSON.parse(rawValue);
    if (Number(parsedValue?.expiresAt || 0) > Date.now()) {
      return true;
    }
  } catch (error) {
    sessionStorage.removeItem(getAdminAuthStorageKey(tabId));
  }

  sessionStorage.removeItem(getAdminAuthStorageKey(tabId));
  return false;
};

const calculateDockScale = () => {
  if (typeof window === 'undefined') {
    return 1;
  }

  const viewportWidth = window.innerWidth || 1440;
  const viewportHeight = window.innerHeight || 900;
  const devicePixelRatio = window.devicePixelRatio || 1;
  const widthScale = viewportWidth / 1440;
  const heightScale = viewportHeight / 900;
  const densityScale = devicePixelRatio > 1.5
    ? Math.max(0.9, 1 - ((devicePixelRatio - 1.5) * 0.08))
    : 1;

  return Math.min(1, Math.max(0.72, Math.min(widthScale, heightScale) * densityScale));
};

const AUXILIARY_APPS = {
  'ai-auth': {
    id: 'ai-auth',
    label: 'Auth Center',
    type: 'window'
  },
  'change-os': {
    id: 'change-os',
    label: 'Change OS',
    type: 'window'
  },
  'profile-settings': {
    id: 'profile-settings',
    label: 'Profile Settings'
  },
  'resume-settings': {
    id: 'resume-settings',
    label: 'Resume Settings'
  },
  'projects-settings': {
    id: 'projects-settings',
    label: 'Projects Settings'
  }
};

const resolveDesktopWallpaper = (settings) => {
  const currentTheme = settings?.theme || 'dark';
  const wallpaper = settings?.themeWallpapers?.[currentTheme] || settings?.wallpaper;

  if (wallpaper === 'night') {
    return 'night';
  }

  if (wallpaper === 'forest') {
    return '/assets/forest.jpg';
  }

  if (typeof wallpaper === 'string' && wallpaper.startsWith('/')) {
    return wallpaper;
  }

  return '/assets/grass.jpg';
};

const resolveWindowFrame = (windowItem, desktopElement, desktopLayout) => {
  if (!desktopElement || !windowItem) {
    return null;
  }

  const safeArea = desktopLayout.safeArea;
  const isCompactViewport = desktopElement.clientWidth <= 900;
  const minimumWindowWidth = isCompactViewport ? 280 : 420;
  const minimumWindowHeight = isCompactViewport ? 260 : 320;
  const compactBottomInset = isCompactViewport ? 18 : safeArea.bottom;
  const widthLimit = Math.max(minimumWindowWidth, desktopElement.clientWidth - safeArea.left - safeArea.right);
  const heightLimit = Math.max(minimumWindowHeight, desktopElement.clientHeight - safeArea.top - compactBottomInset);
  const storedWindowSize = windowItem.windowSize || { width: 600, height: 450 };
  const width = windowItem?.isMaximized
    ? widthLimit
    : Math.min(widthLimit, Math.max(minimumWindowWidth, storedWindowSize.width || 600));
  const height = windowItem?.isMaximized
    ? heightLimit
    : Math.min(heightLimit, Math.max(minimumWindowHeight, storedWindowSize.height || 450));
  const left = windowItem?.isMaximized
    ? safeArea.left
    : Math.min(
        Math.max(windowItem?.windowPosition?.x ?? Math.max(safeArea.left, 120), safeArea.left),
        Math.max(safeArea.left, desktopElement.clientWidth - safeArea.right - width)
      );
  const top = windowItem?.isMaximized
    ? safeArea.top
    : Math.min(
        Math.max(windowItem?.windowPosition?.y ?? Math.max(safeArea.top, 72), safeArea.top),
        Math.max(safeArea.top, desktopElement.clientHeight - safeArea.bottom - height)
      );

  return {
    left,
    top,
    width,
    height
  };
};

const getPortfolioSharePath = (windowId) => {
  if (!['profile', 'resume', 'projects'].includes(windowId)) {
    return '';
  }

  return `/share/${windowId}`;
};

const MOBILE_LAUNCHER_DESCRIPTIONS = {
  settings: 'Personalize theme, language, wallpaper, and profile data.',
  profile: 'View profile, contact details, skills, and introduction.',
  resume: 'Open career history, education, training, and certificates.',
  projects: 'Browse portfolio projects and outcomes.',
  'ai-chat': 'Start an AI-assisted portfolio chat session.',
  'change-os': 'Switch between Win, Mac, and Lin style layouts.'
};

const getMobileLauncherDescription = (icon) => (
  MOBILE_LAUNCHER_DESCRIPTIONS[icon?.id] || (icon?.link ? 'Open this linked portfolio service.' : 'Open this portfolio item.')
);

const Desktop = ({
  config,
  onSettingChange,
  onProfileDataChange,
  onResumeDataChange,
  onProjectsDataChange,
  onDesktopIconsChange,
  onShowToast,
  onQueuePersistToast,
  ensureAdminPortalSession
}) => {
  const [openWindows, setOpenWindows] = useState([]);
  const [focusedWindow, setFocusedWindow] = useState(null);
  const [adminConfigured, setAdminConfigured] = useState(false);
  const [adminStatusLoaded, setAdminStatusLoaded] = useState(false);
  const [isAdminAuthPassEnabled, setIsAdminAuthPassEnabled] = useState(false);
  const [adminTabId, setAdminTabId] = useState('');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [resolvedWallpaper, setResolvedWallpaper] = useState(() => resolveDesktopWallpaper(config.settings));
  const [windowTransition, setWindowTransition] = useState(null);
  const [bouncingDockIconId, setBouncingDockIconId] = useState('');
  const [hiddenWindowId, setHiddenWindowId] = useState('');
  const [dockTooltip, setDockTooltip] = useState(null);
  const [dockScale, setDockScale] = useState(() => calculateDockScale());
  const [aiToolsStatus, setAiToolsStatus] = useState(null);
  const [aiToolsLoading, setAiToolsLoading] = useState(false);
  const [processingWindowIds, setProcessingWindowIds] = useState({});
  const [fileMenuRequest, setFileMenuRequest] = useState(null);
  const [authDialogState, setAuthDialogState] = useState({
    open: false,
    adminId: '',
    adminPw: '',
    error: '',
    loading: false
  });
  const [logoutNotice, setLogoutNotice] = useState({
    open: false,
    title: '',
    message: ''
  });
  const pendingProtectedActionRef = useRef(null);
  const desktopRef = useRef(null);
  const dockNodeRefs = useRef({});
  const transitionTimeoutRef = useRef(null);
  const bounceTimeoutRef = useRef(null);
  const logoutNoticeTimeoutRef = useRef(null);
  const restoreAppRef = useRef(null);
  const minimizeAppRef = useRef(null);
  const autoRestoreImportWindowIdsRef = useRef(new Set());
  const focusedWindowRef = useRef(null);
  const nextWindowLayerRef = useRef(10);
  const activeLanguage = normalizeLanguage(config.settings?.language);
  const osMode = normalizeOsMode(config.settings?.osMode);
  const desktopLayout = resolveDesktopLayout(osMode);
  const desktopIcons = config.desktopIcons || [];
  const profileInfo = config.profileData?.basic_info || {};
  const profileDisplayName = profileInfo.name_en || profileInfo.name_kr || 'DevFolio';
  const profileHeadline = profileInfo.headline || 'Application Architecture && Full Stack Developer';
  const minimizedWindows = openWindows.filter((windowItem) => windowItem.isMinimized);
  const minimizedWindowIds = new Set(minimizedWindows.map((windowItem) => windowItem.id));
  const fixedDockIcons = DOCK_FIXED_IDS
    .map((iconId) => desktopIcons.find((icon) => icon.id === iconId))
    .filter(Boolean);
  const extraDockIcons = desktopIcons.filter((icon) => !DOCK_FIXED_IDS.includes(icon.id));
  const shellIcons = desktopLayout.shortcutsPlacement === 'dock'
    ? [...fixedDockIcons, ...extraDockIcons]
    : desktopIcons;
  const mobileLauncherIcons = desktopIcons;
  const activeWindowLabel = (() => {
    const focusedApp = openWindows.find((windowItem) => windowItem.id === focusedWindow);
    return focusedApp ? getLocalizedAppLabel(focusedApp.id, focusedApp.label, activeLanguage) : 'DevFolio';
  })();
  const targetWallpaper = resolveDesktopWallpaper(config.settings);

  useEffect(() => {
    let nextTabId = sessionStorage.getItem(ADMIN_TAB_ID_STORAGE_KEY);

    if (!nextTabId) {
      nextTabId = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(ADMIN_TAB_ID_STORAGE_KEY, nextTabId);
    }

    setAdminTabId(nextTabId);
    setIsAdminAuthenticated(readAdminSessionState(nextTabId));
  }, []);

  useEffect(() => {
    if (!adminTabId) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      const isStillAuthenticated = readAdminSessionState(adminTabId);
      setIsAdminAuthenticated((previousValue) => (
        previousValue === isStillAuthenticated ? previousValue : isStillAuthenticated
      ));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [adminTabId]);

  useEffect(() => {
    fetch('/api/admin/status')
      .then((response) => response.json())
      .then((data) => {
        setAdminConfigured(Boolean(data.configured));
        setIsAdminAuthPassEnabled(Boolean(data.active_auth_pass));
        setAdminStatusLoaded(true);
      })
      .catch(() => {
        setAdminConfigured(false);
        setIsAdminAuthPassEnabled(false);
        setAdminStatusLoaded(true);
      });
  }, []);

  useEffect(() => {
    focusedWindowRef.current = focusedWindow;
  }, [focusedWindow]);

  const loadAiToolsStatus = useCallback(async (options = {}) => {
    const { silent = false, persist = false } = options;

    if (!silent) {
      setAiToolsLoading(true);
    }

    try {
      const response = await fetch(
        persist ? '/api/ai-tools/status/refresh' : '/api/ai-tools/status',
        persist
          ? {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              }
            }
          : {
              cache: 'no-store'
            }
      );
      const data = await response.json().catch(() => ({}));

      // No guard access key on this browser: AI Chat is gated like Profile/Resume/Projects.
      if (response.status === 401 && data.error === 'guard_auth_required') {
        setAiToolsStatus({ hub: { ok: false, locked: true, error: 'guard_auth_required', targets: {} } });
        return;
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to load AI tool status.');
      }

      setAiToolsStatus(data);
    } catch (error) {
      console.error(error);
      setAiToolsStatus(null);
    } finally {
      if (!silent) {
        setAiToolsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadAiToolsStatus();
  }, [loadAiToolsStatus]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadAiToolsStatus({ silent: true });
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadAiToolsStatus]);

  useEffect(() => {
    const handleWindowFocusRequest = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const targetWindowId = String(event.data?.appId || '').trim();

      if (!targetWindowId) {
        return;
      }

      const isKnownWindow = INTERNAL_WINDOW_IDS.includes(targetWindowId) || Object.prototype.hasOwnProperty.call(AUXILIARY_APPS, targetWindowId);

      if (!isKnownWindow) {
        return;
      }

      setOpenWindows((prevWindows) => {
        const targetWindow = prevWindows.find((windowItem) => windowItem.id === targetWindowId);

        if (!targetWindow) {
          return prevWindows;
        }

        nextWindowLayerRef.current += 1;
        const nextLayer = nextWindowLayerRef.current;

        return prevWindows.map((windowItem) => (
          windowItem.id === targetWindowId
            ? {
                ...windowItem,
                stackOrder: nextLayer
              }
            : windowItem
        ));
      });
      setFocusedWindow(targetWindowId);
    };

    window.addEventListener('message', handleWindowFocusRequest);

    return () => {
      window.removeEventListener('message', handleWindowFocusRequest);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current);
      }

      if (bounceTimeoutRef.current) {
        window.clearTimeout(bounceTimeoutRef.current);
      }

      if (logoutNoticeTimeoutRef.current) {
        window.clearTimeout(logoutNoticeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const highestLayer = openWindows.reduce((maxLayer, windowItem, index) => {
      const layer = Number.isFinite(windowItem?.stackOrder) ? windowItem.stackOrder : index + 1;
      return Math.max(maxLayer, layer);
    }, 10);

    nextWindowLayerRef.current = highestLayer;
  }, [openWindows]);

  useEffect(() => {
    const handleResize = () => {
      setDockScale(calculateDockScale());
      setDockTooltip(null);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (targetWallpaper === 'night') {
      setResolvedWallpaper('night');
      return undefined;
    }

    const image = new Image();
    let isCancelled = false;

    image.onload = () => {
      if (!isCancelled) {
        setResolvedWallpaper(targetWallpaper);
      }
    };

    image.onerror = () => {
      if (!isCancelled) {
        setResolvedWallpaper('/assets/grass.jpg');
      }
    };

    image.src = targetWallpaper;

    return () => {
      isCancelled = true;
    };
  }, [targetWallpaper]);

  const getNextWindowLayer = useCallback(() => {
    nextWindowLayerRef.current += 1;
    return nextWindowLayerRef.current;
  }, []);

  const getTopVisibleWindowId = useCallback((windows) => {
    const topWindow = windows.reduce((selectedWindow, windowItem, index) => {
      if (windowItem.isMinimized) {
        return selectedWindow;
      }

      const layer = Number.isFinite(windowItem?.stackOrder) ? windowItem.stackOrder : index + 1;
      if (!selectedWindow || layer > selectedWindow.layer) {
        return {
          id: windowItem.id,
          layer
        };
      }

      return selectedWindow;
    }, null);

    return topWindow?.id || null;
  }, []);

  const bringWindowToFront = useCallback((windows, id, patch = {}) => {
    const targetWindow = windows.find((windowItem) => windowItem.id === id);

    if (!targetWindow) {
      return windows;
    }

    const nextLayer = getNextWindowLayer();

    return windows.map((windowItem) => (
      windowItem.id === id
        ? {
            ...windowItem,
            ...patch,
            stackOrder: nextLayer
          }
        : windowItem
    ));
  }, [getNextWindowLayer]);

  const updateWindowLayout = useCallback((id, patch) => {
    setOpenWindows((prevWindows) => {
      let hasChanged = false;

      const nextWindows = prevWindows.map((windowItem) => {
        if (windowItem.id !== id) {
          return windowItem;
        }

        const nextWindow = {
          ...windowItem,
          ...patch
        };

        const sizeChanged = Boolean(patch.windowSize) && (
          windowItem.windowSize?.width !== nextWindow.windowSize?.width
          || windowItem.windowSize?.height !== nextWindow.windowSize?.height
        );
        const positionChanged = Boolean(patch.windowPosition) && (
          windowItem.windowPosition?.x !== nextWindow.windowPosition?.x
          || windowItem.windowPosition?.y !== nextWindow.windowPosition?.y
        );

        if (sizeChanged || positionChanged) {
          hasChanged = true;
          return nextWindow;
        }

        return windowItem;
      });

      return hasChanged ? nextWindows : prevWindows;
    });
  }, []);

  const registerDockNode = useCallback((iconId, node) => {
    if (node) {
      dockNodeRefs.current[iconId] = node;
      return;
    }

    delete dockNodeRefs.current[iconId];
  }, []);

  const getDesktopRelativeRect = useCallback((element) => {
    const desktopElement = desktopRef.current;
    if (!desktopElement || !element) {
      return null;
    }

    const desktopRect = desktopElement.getBoundingClientRect();
    const rect = element.getBoundingClientRect();

    return {
      left: rect.left - desktopRect.left,
      top: rect.top - desktopRect.top,
      width: rect.width,
      height: rect.height
    };
  }, []);

  const updateDockTooltip = useCallback((nextTooltip) => {
    if (!nextTooltip?.node) {
      setDockTooltip(null);
      return;
    }

    const rect = getDesktopRelativeRect(nextTooltip.node);

    if (!rect) {
      setDockTooltip(null);
      return;
    }

    setDockTooltip({
      id: nextTooltip.id,
      label: nextTooltip.label,
      left: rect.left + (rect.width / 2),
      top: rect.top - 12
    });
  }, [getDesktopRelativeRect]);

  const triggerDockBounce = useCallback((id) => {
    setBouncingDockIconId(id);
    if (bounceTimeoutRef.current) {
      window.clearTimeout(bounceTimeoutRef.current);
    }

    bounceTimeoutRef.current = window.setTimeout(() => {
      setBouncingDockIconId('');
      bounceTimeoutRef.current = null;
    }, 540);
  }, []);

  const runTransition = useCallback(({ id, type, startRect, endRect }) => {
    if (!startRect || !endRect) {
      return false;
    }

    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
    }

    setHiddenWindowId(id);
    setWindowTransition({
      id,
      type,
      startRect,
      endRect,
      phase: 'idle'
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setWindowTransition((prev) => (prev ? { ...prev, phase: 'active' } : prev));
      });
    });

    return true;
  }, []);

  const setAdminSessionAuthenticated = useCallback((nextValue) => {
    if (!adminTabId) {
      return;
    }

    const storageKey = getAdminAuthStorageKey(adminTabId);

    if (nextValue) {
      sessionStorage.setItem(storageKey, JSON.stringify({
        authenticated: true,
        expiresAt: Date.now() + ADMIN_AUTH_TTL_MS
      }));
    } else {
      sessionStorage.removeItem(storageKey);
    }

    setIsAdminAuthenticated(nextValue);
  }, [adminTabId]);

  const closeAuthDialog = useCallback(() => {
    pendingProtectedActionRef.current = null;
    setAuthDialogState({
      open: false,
      adminId: '',
      adminPw: '',
      error: '',
      loading: false
    });
  }, []);

  // OTP redesign: admin-protected actions are gated by the OTP portal session, not by the
  // legacy ID/PW dialog. ensureAdminPortalSession() prompts the OTP login when needed and
  // resolves true once a server-enforced session cookie is in place.
  const requestAdminAccess = useCallback(async (action) => {
    if (typeof ensureAdminPortalSession !== 'function') {
      // Defensive fallback: without the gate prop, do not silently expose admin actions.
      onShowToast?.('Admin authentication is unavailable.', 'error');
      return;
    }

    const authenticated = await ensureAdminPortalSession();
    if (authenticated) {
      action?.();
    }
  }, [ensureAdminPortalSession, onShowToast]);

  useEffect(() => {
    if (!adminStatusLoaded || !authDialogState.open || !pendingProtectedActionRef.current) {
      return;
    }

    if (isAdminAuthPassEnabled || isAdminAuthenticated) {
      const pendingAction = pendingProtectedActionRef.current;
      closeAuthDialog();
      pendingAction?.();
      return;
    }

    setAuthDialogState((prev) => ({
      ...prev,
      loading: false,
      error: ''
    }));
  }, [adminStatusLoaded, authDialogState.open, closeAuthDialog, isAdminAuthPassEnabled, isAdminAuthenticated]);

  const closeApp = useCallback((id) => {
    setOpenWindows((prevWindows) => {
      const nextWindows = prevWindows.filter((windowItem) => windowItem.id !== id);
      setFocusedWindow(getTopVisibleWindowId(nextWindows));
      return nextWindows;
    });
  }, [getTopVisibleWindowId]);

  const focusApp = useCallback((id) => {
    setOpenWindows((prevWindows) => {
      const targetWindow = prevWindows.find((windowItem) => windowItem.id === id);

      if (!targetWindow || targetWindow.isMinimized) {
        return prevWindows;
      }

      const currentTopWindowId = getTopVisibleWindowId(prevWindows);
      if (currentTopWindowId === id && focusedWindowRef.current === id) {
        return prevWindows;
      }

      return bringWindowToFront(prevWindows, id);
    });
    setFocusedWindow((currentFocusedWindow) => (currentFocusedWindow === id ? currentFocusedWindow : id));
  }, [bringWindowToFront, getTopVisibleWindowId]);

  const dismissLogoutNotice = useCallback(() => {
    if (logoutNoticeTimeoutRef.current) {
      window.clearTimeout(logoutNoticeTimeoutRef.current);
      logoutNoticeTimeoutRef.current = null;
    }

    setLogoutNotice({
      open: false,
      title: '',
      message: ''
    });
  }, []);

  const handleAdminLogout = useCallback(({ closeWindowId } = {}) => {
    if (!adminTabId) {
      return;
    }

    setAdminSessionAuthenticated(false);
    setAuthDialogState({
      open: false,
      adminId: '',
      adminPw: '',
      error: '',
      loading: false
    });
    pendingProtectedActionRef.current = null;

    if (closeWindowId) {
      closeApp(closeWindowId);
    }

    if (logoutNoticeTimeoutRef.current) {
      window.clearTimeout(logoutNoticeTimeoutRef.current);
    }

    setLogoutNotice({
      open: true,
      title: 'Logged Out',
      message: 'Browser auth session cleared. This confirmation window will close automatically in 3 seconds.'
    });

    logoutNoticeTimeoutRef.current = window.setTimeout(() => {
      setLogoutNotice({
        open: false,
        title: '',
        message: ''
      });
      logoutNoticeTimeoutRef.current = null;
    }, 3000);
  }, [adminTabId, closeApp, setAdminSessionAuthenticated]);

  const openWindow = useCallback((id) => {
    setDockTooltip(null);
    const appInfo = config.desktopIcons.find((icon) => icon.id === id) || AUXILIARY_APPS[id];

    if (!appInfo) {
      return;
    }

    const existingWindow = openWindows.find((windowItem) => windowItem.id === id);
    if (existingWindow?.isMinimized) {
      restoreAppRef.current?.(id);
      return;
    }

    setOpenWindows((prevWindows) => {
      const isAlreadyOpen = prevWindows.find((windowItem) => windowItem.id === id);

      if (!isAlreadyOpen) {
        return [...prevWindows, {
          ...appInfo,
          isMinimized: false,
          isMaximized: false,
          stackOrder: getNextWindowLayer()
        }];
      }

      return bringWindowToFront(prevWindows, id, { isMinimized: false });
    });
    setFocusedWindow(id);
  }, [bringWindowToFront, config.desktopIcons, getNextWindowLayer, openWindows]);

  const restoreApp = useCallback((id) => {
    const dockNode = dockNodeRefs.current[id];
    const startRect = getDesktopRelativeRect(dockNode);
    const targetWindow = openWindows.find((windowItem) => windowItem.id === id);
    const desktopElement = desktopRef.current;
      const endRect = (() => {
        if (!desktopElement || !targetWindow) {
          return null;
        }

        if (targetWindow.restoreFrame) {
          return targetWindow.restoreFrame;
        }

        return resolveWindowFrame(targetWindow, desktopElement, desktopLayout);
      })();

    if (runTransition({ id, type: 'restore', startRect, endRect })) {
      setOpenWindows((prevWindows) => bringWindowToFront(prevWindows, id, {
        isMinimized: false,
        restoreFrame: undefined
      }));
      setFocusedWindow(id);
      transitionTimeoutRef.current = window.setTimeout(() => {
        setWindowTransition(null);
        setHiddenWindowId('');
        transitionTimeoutRef.current = null;
      }, RESTORE_ANIMATION_MS);
      return;
    }

    setOpenWindows((prevWindows) => bringWindowToFront(prevWindows, id, {
      isMinimized: false,
      restoreFrame: undefined
    }));
    setFocusedWindow(id);
  }, [bringWindowToFront, desktopLayout, getDesktopRelativeRect, openWindows, runTransition]);

  restoreAppRef.current = restoreApp;

  const openApp = useCallback((id) => {
    const appInfo = config.desktopIcons.find((icon) => icon.id === id) || AUXILIARY_APPS[id];

    if (!appInfo) {
      return;
    }

    const isInternalWindow = INTERNAL_WINDOW_IDS.includes(id) || appInfo.type === 'window' || Object.prototype.hasOwnProperty.call(AUXILIARY_APPS, id);

    if (!isInternalWindow && Object.prototype.hasOwnProperty.call(appInfo, 'link')) {
      if (appInfo.link) {
        window.open(appInfo.link, '_blank');
      }
      return;
    }

    if (id === 'settings' || id === 'ai-auth') {
      requestAdminAccess(() => openWindow(id));
      return;
    }

    openWindow(id);
  }, [config.desktopIcons, openWindow, requestAdminAccess]);

  // The project galleries, shared by both Projects windows. An upload or a render started in
  // the editor has to appear in the viewer straight away; keeping the map inside each window
  // meant the viewer only learned about it by being closed and reopened (its own fetch).
  const [projectImages, setProjectImages] = useState({});
  const [projectImageLimit, setProjectImageLimit] = useState(30);

  // Which project the Projects window is showing. The editor opens on that same project and
  // moves with it, so "Edit" always means "edit the one I am looking at" — the two windows are
  // separate component instances, so the index has to live above both of them.
  const [projectViewIndex, setProjectViewIndex] = useState(0);

  const handleProtectedMenuAction = useCallback((menuId, editorTarget) => {
    if (menuId === 'window' && focusedWindow) {
      const sharePath = getPortfolioSharePath(focusedWindow);
      if (sharePath) {
        const shareWindow = window.open(sharePath, '_blank', 'noopener,noreferrer');
        shareWindow?.focus?.();
      }
      return;
    }

    requestAdminAccess(() => {
      if (menuId === 'file' && focusedWindow && ['profile', 'resume', 'projects'].includes(focusedWindow)) {
        setFileMenuRequest({
          targetWindowId: focusedWindow,
          nonce: Date.now()
        });
        return;
      }

      if (menuId === 'edit' && editorTarget) {
        openWindow(editorTarget);
      }
    });
  }, [focusedWindow, openWindow, requestAdminAccess]);

  const requestProtectedFileImport = useCallback((request) => {
    const targetWindowId = typeof request === 'string'
      ? request
      : String(request?.targetWindowId || '').trim();
    if (!['profile', 'resume', 'projects'].includes(targetWindowId)) {
      return;
    }

    requestAdminAccess(() => {
      openWindow(targetWindowId);
      setFileMenuRequest({
        targetWindowId,
        nonce: Date.now(),
        continuationSourceId: typeof request === 'object'
          ? String(request?.continuationSourceId || '').trim()
          : ''
      });
    });
  }, [openWindow, requestAdminAccess]);

  const handlePortfolioImportProcessingChange = useCallback(({ targetWindowId, isProcessing }) => {
    if (!['profile', 'resume', 'projects'].includes(targetWindowId)) {
      return;
    }

    const targetWindow = openWindows.find((windowItem) => windowItem.id === targetWindowId);

    if (isProcessing) {
      setProcessingWindowIds((prevState) => {
        if (prevState[targetWindowId]) {
          return prevState;
        }

        return {
          ...prevState,
          [targetWindowId]: true
        };
      });

      if (targetWindow && !targetWindow.isMinimized) {
        autoRestoreImportWindowIdsRef.current.add(targetWindowId);
        minimizeAppRef.current?.(targetWindowId);
      }
      return;
    }

    const shouldAutoRestore = autoRestoreImportWindowIdsRef.current.has(targetWindowId);
    if (shouldAutoRestore) {
      autoRestoreImportWindowIdsRef.current.delete(targetWindowId);

      if (targetWindow?.isMinimized) {
        restoreAppRef.current?.(targetWindowId);
      }
    }

    setProcessingWindowIds((prevState) => {
      if (!prevState[targetWindowId]) {
        return prevState;
      }

      const nextState = { ...prevState };
      delete nextState[targetWindowId];
      return nextState;
    });
  }, [openWindows]);

  const handleAuthSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!adminStatusLoaded) {
      setAuthDialogState((prev) => ({
        ...prev,
        error: 'Admin status is still loading.'
      }));
      return;
    }

    const adminId = authDialogState.adminId.trim();
    const adminPw = authDialogState.adminPw;

    if (!adminId || !adminPw) {
      setAuthDialogState((prev) => ({
        ...prev,
        error: 'Admin ID and password are required.'
      }));
      return;
    }

    const endpoint = adminConfigured ? '/api/admin/verify' : '/api/admin/setup';

    try {
      setAuthDialogState((prev) => ({
        ...prev,
        loading: true,
        error: ''
      }));

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          admin_id: adminId,
          admin_pw: adminPw
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify admin credentials.');
      }

      setAdminConfigured(true);
      setAdminSessionAuthenticated(true);

      const pendingAction = pendingProtectedActionRef.current;
      closeAuthDialog();
      pendingAction?.();
    } catch (error) {
      setAuthDialogState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to verify admin credentials.'
      }));
    }
  }, [adminConfigured, adminStatusLoaded, authDialogState.adminId, authDialogState.adminPw, closeAuthDialog, setAdminSessionAuthenticated]);

  const handleAdminAuthPassToggle = useCallback(async (nextValue) => {
    try {
      const response = await fetch('/api/admin/auth-pass', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          active_auth_pass: nextValue
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update auth_pass.');
      }

      setIsAdminAuthPassEnabled(Boolean(data.active_auth_pass));

      if (data.active_auth_pass) {
        setAdminSessionAuthenticated(false);
      }
    } catch (error) {
      console.error(error);
    }
  }, [setAdminSessionAuthenticated]);

  const minimizeApp = useCallback((id) => {
    const windowNode = document.querySelector(`[data-window-id="${id}"]`);
    const dockNode = dockNodeRefs.current[id];
    const startRect = getDesktopRelativeRect(windowNode);
    const endRect = osMode === 'win'
      ? (() => {
          const desktopElement = desktopRef.current;
          if (!desktopElement) {
            return null;
          }

          const desktopRect = desktopElement.getBoundingClientRect();
          const shellHeight = 56;
          const targetWidth = 180;
          const targetHeight = 28;

          return {
            left: Math.max(24, (desktopRect.width / 2) - (targetWidth / 2)),
            top: Math.max(24, desktopRect.height - shellHeight + ((shellHeight - targetHeight) / 2)),
            width: targetWidth,
            height: targetHeight
          };
        })()
      : getDesktopRelativeRect(dockNode);
    setFocusedWindow(null);

    if (runTransition({ id, type: 'minimize', startRect, endRect })) {
      transitionTimeoutRef.current = window.setTimeout(() => {
        setOpenWindows((prevWindows) => {
          const nextWindows = bringWindowToFront(prevWindows, id, {
            isMinimized: true,
            restoreFrame: startRect || undefined
          });
          setFocusedWindow(getTopVisibleWindowId(nextWindows));

          return nextWindows;
        });
        setWindowTransition(null);
        setHiddenWindowId('');
        if (osMode !== 'win') {
          triggerDockBounce(id);
        }
        transitionTimeoutRef.current = null;
      }, MINIMIZE_ANIMATION_MS);
      return;
    }

    setOpenWindows((prevWindows) => {
      const nextWindows = bringWindowToFront(prevWindows, id, {
        isMinimized: true,
        restoreFrame: startRect || undefined
      });
      setFocusedWindow(getTopVisibleWindowId(nextWindows));

      return nextWindows;
    });
  }, [bringWindowToFront, getDesktopRelativeRect, getTopVisibleWindowId, osMode, runTransition, triggerDockBounce]);

  minimizeAppRef.current = minimizeApp;

  const toggleMaximizeApp = useCallback((id) => {
    setOpenWindows((prevWindows) => {
      const targetWindow = prevWindows.find((windowItem) => windowItem.id === id);
      if (!targetWindow) {
        return prevWindows;
      }

      return bringWindowToFront(prevWindows, id, {
        isMaximized: !targetWindow.isMaximized,
        isMinimized: false
      });
    });
    setFocusedWindow(id);
  }, [bringWindowToFront]);

  const wallpaperStyle = (() => {
    if (resolvedWallpaper === 'night') {
      return {
        background: 'linear-gradient(135deg, #09111f 0%, #1a1a2e 45%, #2f1b3d 100%)'
      };
    }

    if (typeof resolvedWallpaper === 'string' && resolvedWallpaper.startsWith('/')) {
      return {
        backgroundImage: `url(${resolvedWallpaper})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }

    return {
      backgroundImage: 'url(/assets/grass.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    };
  })();
  // Any chat target the ai-chat-hub can serve right now (NVIDIA / Claude / Antigravity / Codex).
  const hasAuthenticatedAiSession = Object.values(aiToolsStatus?.hub?.targets || {}).some((target) => target?.available);
  const clearFileMenuRequest = useCallback(() => {
    setFileMenuRequest(null);
  }, []);
  const linShellShortcutsNode = osMode === 'lin' ? (
    <div className="shell-shortcuts-inline shell-shortcuts-inline-lin" aria-label="Application shortcuts">
      {shellIcons.map((icon) => (
        <Icon
          key={icon.id}
          icon={icon}
          language={activeLanguage}
          onOpen={openApp}
          variant="desktop"
          registerDockNode={registerDockNode}
          isBouncing={bouncingDockIconId === icon.id}
          isMinimized={minimizedWindowIds.has(icon.id)}
          isProcessing={Boolean(processingWindowIds[icon.id])}
        />
      ))}
    </div>
  ) : null;
  const winShellShortcutsNode = osMode === 'win' ? (
    <div className="shell-shortcuts-inline shell-shortcuts-inline-win" aria-label="Application shortcuts">
      {shellIcons.map((icon) => (
        <Icon
          key={icon.id}
          icon={icon}
          language={activeLanguage}
          onOpen={openApp}
          variant="dock"
          registerDockNode={registerDockNode}
          isBouncing={bouncingDockIconId === icon.id}
          isMinimized={minimizedWindowIds.has(icon.id)}
          isProcessing={Boolean(processingWindowIds[icon.id])}
        />
      ))}
    </div>
  ) : null;
  const mobileLauncherNode = (
    <section className="mobile-launcher" aria-label="Mobile portfolio launcher">
      <div className="mobile-launcher-hero">
        <div className="mobile-launcher-kicker">DevFolio</div>
        <h1>{profileDisplayName}</h1>
        <p>{profileHeadline}</p>
      </div>
      <div className="mobile-launcher-grid" role="list">
        {mobileLauncherIcons.map((icon) => (
          <div
            className="mobile-launcher-card"
            role="button"
            tabIndex={0}
            key={icon.id}
            onClick={() => openApp(icon.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openApp(icon.id);
              }
            }}
          >
            <Icon
              icon={icon}
              language={activeLanguage}
              onOpen={openApp}
              variant="desktop"
              registerDockNode={registerDockNode}
              isBouncing={bouncingDockIconId === icon.id}
              isMinimized={minimizedWindowIds.has(icon.id)}
              isProcessing={Boolean(processingWindowIds[icon.id])}
            />
            <p>{getMobileLauncherDescription(icon)}</p>
          </div>
        ))}
      </div>
    </section>
  );

  return (
    <div ref={desktopRef} className={`desktop desktop-os-${osMode}`} style={wallpaperStyle}>
      <TopBar
        creator="SSS from ArchiDev"
        version={config?.version || ''}
        theme={config.settings.theme || 'dark'}
        language={activeLanguage}
        osMode={osMode}
        onToggleTheme={(nextTheme) => onSettingChange('theme', nextTheme)}
        onOpenSettings={() => openApp('settings')}
        focusedWindowId={focusedWindow}
        activeWindowLabel={activeWindowLabel}
        onProtectedMenuAction={handleProtectedMenuAction}
        hasAuthenticatedAiSession={hasAuthenticatedAiSession}
        minimizedWindows={minimizedWindows}
        onRestoreWindow={restoreApp}
        onOpenApp={openApp}
        shellShortcutNode={osMode === 'win' ? winShellShortcutsNode : linShellShortcutsNode}
        shellShortcutItems={shellIcons}
      />

      {mobileLauncherNode}

      {desktopLayout.shortcutsPlacement === 'dock' ? (
        <div
          className="dock-shell"
          style={{
            '--dock-scale': dockScale
          }}
        >
          <div className="dock-scroll" role="toolbar" aria-label="Application shortcuts">
            <div className="dock-group dock-group-fixed">
              {fixedDockIcons.map((icon) => (
                <Icon
                  key={icon.id}
                  icon={icon}
                  language={activeLanguage}
                  onOpen={openApp}
                  variant="dock"
                  registerDockNode={registerDockNode}
                  isBouncing={bouncingDockIconId === icon.id}
                  isMinimized={minimizedWindowIds.has(icon.id)}
                  isProcessing={Boolean(processingWindowIds[icon.id])}
                  onDockTooltipChange={updateDockTooltip}
                />
              ))}
            </div>
            {extraDockIcons.length > 0 ? <div className="dock-divider" aria-hidden="true"></div> : null}
            {extraDockIcons.length > 0 ? (
              <div className="dock-group dock-group-extra">
                {extraDockIcons.map((icon) => (
                  <Icon
                    key={icon.id}
                    icon={icon}
                    language={activeLanguage}
                    onOpen={openApp}
                    variant="dock"
                    registerDockNode={registerDockNode}
                    isBouncing={bouncingDockIconId === icon.id}
                    isMinimized={minimizedWindowIds.has(icon.id)}
                    isProcessing={Boolean(processingWindowIds[icon.id])}
                    onDockTooltipChange={updateDockTooltip}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {dockTooltip ? (
        <div
          className="dock-tooltip-layer"
          style={{
            left: `${dockTooltip.left}px`,
            top: `${dockTooltip.top}px`
          }}
          aria-hidden="true"
        >
          {dockTooltip.label}
        </div>
      ) : null}

      {windowTransition ? (
        <div
          className={`window-transition-overlay window-transition-${windowTransition.type} ${windowTransition.phase === 'active' ? 'active' : ''}`.trim()}
          style={{
            '--transition-start-left': `${windowTransition.startRect.left}px`,
            '--transition-start-top': `${windowTransition.startRect.top}px`,
            '--transition-start-width': `${windowTransition.startRect.width}px`,
            '--transition-start-height': `${windowTransition.startRect.height}px`,
            '--transition-delta-x': `${windowTransition.endRect.left - windowTransition.startRect.left}px`,
            '--transition-delta-y': `${windowTransition.endRect.top - windowTransition.startRect.top}px`,
            '--transition-scale-x': `${windowTransition.endRect.width / Math.max(windowTransition.startRect.width, 1)}`,
            '--transition-scale-y': `${windowTransition.endRect.height / Math.max(windowTransition.startRect.height, 1)}`
          }}
          aria-hidden="true"
        >
          <div className="window-transition-header"></div>
        </div>
      ) : null}

      {openWindows.map((app) => (
        (app.isMinimized && !processingWindowIds[app.id]) ? null : (() => {
          const needsAiToolStatus = AI_TOOL_WINDOW_IDS.includes(app.id);
          const keepMinimizedWindowMounted = app.isMinimized && Boolean(processingWindowIds[app.id]);

          return (
            <Window
              key={app.id}
              app={app}
              config={config}
              onClose={closeApp}
              onFocus={focusApp}
              onMinimize={minimizeApp}
              onToggleMaximize={toggleMaximizeApp}
              isFocused={focusedWindow === app.id}
              isMaximized={app.isMaximized}
              onSettingChange={onSettingChange}
              onProfileDataChange={onProfileDataChange}
              onResumeDataChange={onResumeDataChange}
              onProjectsDataChange={onProjectsDataChange}
              onDesktopIconsChange={onDesktopIconsChange}
              onOpenApp={openApp}
              onWindowLayoutChange={updateWindowLayout}
              isAdminAuthPassEnabled={isAdminAuthPassEnabled}
              isAdminAuthenticated={isAdminAuthenticated}
              onAdminAuthPassToggle={handleAdminAuthPassToggle}
              onAdminLogout={handleAdminLogout}
              aiToolsStatus={needsAiToolStatus ? aiToolsStatus : null}
              aiToolsLoading={needsAiToolStatus ? aiToolsLoading : false}
              onRefreshAiToolsStatus={needsAiToolStatus ? loadAiToolsStatus : null}
              fileMenuRequest={fileMenuRequest}
              onConsumeFileMenuRequest={clearFileMenuRequest}
              isVisuallyHidden={hiddenWindowId === app.id || keepMinimizedWindowMounted}
              onShowToast={onShowToast}
              onQueuePersistToast={onQueuePersistToast}
              osMode={osMode}
              desktopLayout={desktopLayout}
              onProtectedMenuAction={handleProtectedMenuAction}
              onRequestProtectedFileImport={requestProtectedFileImport}
              onPortfolioImportProcessingChange={handlePortfolioImportProcessingChange}
              ensureAdminPortalSession={ensureAdminPortalSession}
              projectViewIndex={projectViewIndex}
              onProjectViewIndexChange={setProjectViewIndex}
              projectImages={projectImages}
              onProjectImagesChange={setProjectImages}
              projectImageLimit={projectImageLimit}
              onProjectImageLimitChange={setProjectImageLimit}
            />
          );
        })()
      ))}

      {authDialogState.open ? (
        <div className="admin-auth-backdrop" onClick={closeAuthDialog}>
          <div
            className="admin-auth-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>{adminConfigured ? 'Admin Authentication' : 'Register Admin'}</h3>
            <p>
              {adminConfigured
                ? 'Enter the administrator ID and password to continue.'
                : 'No administrator is configured yet. Enter a new administrator ID and password to initialize admin.json.'}
            </p>
            <form className="admin-auth-form" onSubmit={handleAuthSubmit}>
              <label>
                <span>Admin ID</span>
                <input
                  type="text"
                  value={authDialogState.adminId}
                  onChange={(event) => setAuthDialogState((prev) => ({ ...prev, adminId: event.target.value }))}
                  autoFocus
                  autoComplete="off"
                  name="desktop-admin-id"
                />
              </label>
              <label>
                <span>Admin Password</span>
                <input
                  type="password"
                  value={authDialogState.adminPw}
                  onChange={(event) => setAuthDialogState((prev) => ({ ...prev, adminPw: event.target.value }))}
                  autoComplete="new-password"
                  name="desktop-admin-password"
                />
              </label>
              {authDialogState.error ? <p className="admin-auth-error">{authDialogState.error}</p> : null}
              <div className="admin-auth-actions">
                <button type="button" className="admin-auth-secondary" onClick={closeAuthDialog} disabled={authDialogState.loading}>
                  Cancel
                </button>
                <button type="submit" className="admin-auth-primary" disabled={authDialogState.loading}>
                  {authDialogState.loading ? 'Processing...' : adminConfigured ? 'Authenticate' : 'Save Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {logoutNotice.open ? (
        <div className="admin-auth-backdrop" onClick={dismissLogoutNotice}>
          <div
            className="admin-auth-modal admin-confirm-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>{logoutNotice.title}</h3>
            <p>{logoutNotice.message}</p>
            <div className="admin-auth-actions">
              <button type="button" className="admin-auth-primary" onClick={dismissLogoutNotice}>
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Desktop;
